import React, { useState, useEffect } from 'react'
import { ContractVehicleDropdown } from './ContractVehicleDropdown'
import { ContractDropdown } from './ContractDropdown'
import type { ContractType, ContractVehicleType, SupplierType } from './types'

interface InputSelectors {
  contractVehicle: string
  contract: string
  contractFreeform: string
}

interface SelectContractProps {
  contractVehicles?: ContractVehicleType[]
  contract?: ContractType | null
  contractVehicle?: ContractVehicleType | null
  supplier?: SupplierType | null
  freeform?: string | null
  inputSelectors?: InputSelectors
}

const defaultInputSelectors: InputSelectors = {
  contractVehicle: '#review_contract_vehicle_id',
  contract: '#review_contract_id',
  contractFreeform: '#review_contract_freeform'
}

const SelectContract: React.FC<SelectContractProps> = ({ contractVehicles = [], contract = null, contractVehicle = null, supplier = null, freeform = null, inputSelectors = defaultInputSelectors }) => {
  const prefillData = (): { prefilledCV: ContractVehicleType | null, prefilledContract: ContractType | null } => {
    let prefilledContract: ContractType | null = null
    let prefilledCV: ContractVehicleType | null = null

    // if both are provided, ensure contract is a part of contract vehicle
    if (contract != null && contractVehicle != null) {
      if (contractVehicle.contracts.some(c => c.id === contract.id)) {
        prefilledCV = contractVehicle
        prefilledContract = contract
      }
    // if only contract is provided, find parent contract vehicle
    } else if (contract != null) {
      const parentCV = contractVehicles.find(cv => cv.contracts.some(c => c.id === contract.id))
      if (parentCV != null) {
        prefilledCV = parentCV
        prefilledContract = contract
      }
    // if only contract vehicle is provided, then set it
    } else if (contractVehicle != null) {
      prefilledCV = contractVehicle
    }

    return { prefilledCV, prefilledContract }
  }

  const { prefilledCV, prefilledContract } = prefillData()

  const checkIfPrefilled = (): boolean => {
    return (prefilledCV != null || prefilledContract != null) && supplier != null
  }

  const [chosenSupplier, setChosenSupplier] = useState<SupplierType | null>(supplier)
  const [selectableContractVehicles, setSelectableContractVehicles] = useState<ContractVehicleType[]>(contractVehicles)
  const [addingContract, setAddingContract] = useState<boolean>(checkIfPrefilled())
  const [selectedContractVehicle, setSelectedContractVehicle] = useState<ContractVehicleType | null>(prefilledCV)
  const [selectedContract, setSelectedContract] = useState<ContractType | null>(prefilledContract)
  const [freeformContract, setFreeformContract] = useState<string | null>(freeform)
  const [notListed, setNotListed] = useState(false)

  useEffect(() => {
    $(inputSelectors.contract).val(`${selectedContract?.id ?? ''}`).change()
    $(inputSelectors.contractVehicle).val(`${selectedContractVehicle?.id ?? ''}`).change()
    $(inputSelectors.contractFreeform).val(`${freeformContract ?? ''}`).change()
  }, [selectedContract, selectedContractVehicle, freeformContract])

  // ----- BEGIN Handle when chosen supplier changes -----

  const setChosenSupplierData = (): void => {
    const { supplier: s, contractVehicles: cvs } = $('#chosen-supplier-data').data() as { supplier: SupplierType | null, contractVehicles: ContractVehicleType[] }
    setSelectableContractVehicles(cvs)
    setChosenSupplier(s)
    handleRemoveContract()
  }

  const listenForChosenSupplierChange = (): MutationObserver => {
    const observer = new MutationObserver(() => setChosenSupplierData())
    const target = document.getElementById('chosen-supplier-data')
    if (target != null) {
      observer.observe(target, { attributes: true })
    }
    return observer
  }

  useEffect(() => {
    const observer = listenForChosenSupplierChange()
    return () => {
      observer.disconnect()
    }
  }, [])

  // ----- END Handle when chosen supplier changes -----

  const handleInitialClick = (): void => {
    setAddingContract(true)
  }

  const handleRemoveContract = (): void => {
    setAddingContract(false)
    setSelectedContractVehicle(null)
    setSelectedContract(null)
    setFreeformContract('')
    setNotListed(false)
  }

  const renderLabel = (): React.JSX.Element => {
    return (
      <div className='label-title'>
        <label className='sub-header'>Contract</label>
        {addingContract ? <a onClick={handleRemoveContract} id='remove-contract-link'>Remove Contract</a> : null}
      </div>
    )
  }

  const renderInitialPrompt = (): React.JSX.Element => {
    const disabled = chosenSupplier == null || selectableContractVehicles.length === 0
    return (
      <button id='add-prompt' type='button' className={disabled ? 'disabled' : undefined} onClick={disabled ? undefined : handleInitialClick} disabled={disabled}>
        <span className='fa-stack add-prompt-icon'>
          <i className='fa-stack-2x fa-thin fa-circle' />
          <i className='fa-stack-1x fa-solid fa-plus' />
        </span>
        <span className='add-prompt-text'>
          <strong>Add Contract Information</strong> (Optional)
        </span>
      </button>
    )
  }

  const handleAddContractVehicleClick = (cv: ContractVehicleType): void => {
    setSelectedContractVehicle(cv)
    setSelectedContract(null)
    setFreeformContract('')
  }

  const handleAddContractClick = (contract: ContractType): void => {
    setSelectedContract(contract)
    setFreeformContract('')
  }

  const handleNotListedClick = (): void => {
    setNotListed(true)
    setSelectedContract(null)
  }

  const handleFreeformContract = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFreeformContract(event.target.value)
  }

  const handleUnselectContract = (): void => {
    setSelectedContract(null)
  }

  const handleUnselectContractVehicle = (): void => {
    setSelectedContractVehicle(null)
    setSelectedContract(null)
  }

  const renderDropdownSelects = (): React.JSX.Element => {
    return (
      <>
        <ContractVehicleDropdown
          contractVehicles={selectableContractVehicles}
          selectedContractVehicle={selectedContractVehicle}
          handleAddContractVehicleClick={handleAddContractVehicleClick}
          handleUnselectContractVehicle={handleUnselectContractVehicle}
        />
        <ContractDropdown
          selectedContract={selectedContract}
          selectedContractVehicle={selectedContractVehicle}
          freeformContract={freeformContract}
          notListed={notListed}
          handleFreeformContract={handleFreeformContract}
          handleNotListedClick={handleNotListedClick}
          handleAddContractClick={handleAddContractClick}
          handleUnselectContract={handleUnselectContract}
        />
      </>
    )
  }

  return (
    <div id='link-contract-review'>
      {renderLabel()}
      {addingContract ? renderDropdownSelects() : renderInitialPrompt()}
    </div>
  )
}

export default SelectContract
